import styled from "@emotion/styled";
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import { APP_BREAKPOINT } from "src/constants/layout";

export interface OtpInputProps {
  onComplete: (code: string) => void;
  disabled?: boolean;
  error?: boolean;
}

export interface OtpInputRef {
  clear: () => void;
  focus: () => void;
}

const OTP_LENGTH = 6;

const InputContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
`;

const DigitInputWrap = styled.div<{ "data-error"?: boolean }>`
  width: 3rem;
  height: 4rem;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--Greys-White, #fff);
  transition: all 0.2s ease-in-out;
  border-radius: 8px;
  border: ${(props) =>
    props["data-error"]
      ? "2px solid var(--primary-warning-red, #bf1902)"
      : "1px solid var(--Greys-Charcoal60, rgba(33, 33, 33, 0.6))"};
  box-sizing: border-box;

  @media (min-width: ${APP_BREAKPOINT}px) {
    width: 4rem;
    height: 4.25rem;
  }

  &:focus-within {
    border: 2px solid transparent;
    background:
      linear-gradient(white, white) padding-box,
      var(
          --input-border-bg,
          linear-gradient(46deg, #ffd199 13.49%, #fad0e8 50.42%, #a6c6ff 85.11%)
        )
        border-box;
    box-shadow:
      0px 0px 0px 0px rgba(69, 49, 22, 0.08),
      -5px 5px 16px 0px rgba(69, 49, 22, 0.08),
      -20px 20px 29px 0px rgba(69, 49, 22, 0.07),
      -46px 45px 39px 0px rgba(69, 49, 22, 0.03),
      -82px 81px 46px 0px rgba(69, 49, 22, 0.01);
  }

  &[data-disabled="true"] {
    opacity: 0.7;
    filter: grayscale(1);
    cursor: not-allowed;
  }
`;

const DigitInput = styled.input`
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 3;
  border: none;
  background: transparent;
  color: var(--Greys-Charcoal, #212121);
  text-align: center;
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-family: "STK Bureau";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  letter-spacing: -0.32px;
  outline: none;
  caret-color: transparent;
  border-radius: 0.5rem;
  padding: 0;

  &:disabled {
    cursor: not-allowed;
  }

  /* Hide number input spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const OtpInput = forwardRef<OtpInputRef, OtpInputProps>(
  ({ onComplete, disabled = false, error = false }, ref) => {
    const [values, setValues] = useState<string[]>(Array(OTP_LENGTH).fill(""));
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const clear = useCallback(() => {
      setValues(Array(OTP_LENGTH).fill(""));
      // Use setTimeout to ensure the state update is processed before focusing
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 0);
    }, []);

    const focus = useCallback(() => {
      const firstEmpty = values.findIndex((v) => !v);
      const indexToFocus = firstEmpty === -1 ? OTP_LENGTH - 1 : firstEmpty;
      inputRefs.current[indexToFocus]?.focus();
    }, [values]);

    useImperativeHandle(
      ref,
      () => ({
        clear,
        focus
      }),
      [clear, focus]
    );

    const handleChange = useCallback(
      (index: number, value: string) => {
        // Only allow digits
        const digits = value.replace(/\D/g, "");
        if (!digits && value) return;

        // Handle multi-digit input (e.g. paste via onChange on mobile browsers)
        if (digits.length > 1) {
          const newValues = [...values];
          const chars = digits.slice(0, OTP_LENGTH - index).split("");
          chars.forEach((digit, i) => {
            if (index + i < OTP_LENGTH) {
              newValues[index + i] = digit;
            }
          });
          setValues(newValues);

          const nextEmpty = newValues.findIndex((v) => !v);
          if (newValues.every((v) => v)) {
            onComplete(newValues.join(""));
            inputRefs.current[OTP_LENGTH - 1]?.focus();
          } else {
            inputRefs.current[
              nextEmpty !== -1 ? nextEmpty : OTP_LENGTH - 1
            ]?.focus();
          }
          return;
        }

        const newValues = [...values];
        newValues[index] = digits;
        setValues(newValues);

        // Move to next field if a digit was entered
        if (digits && index < OTP_LENGTH - 1) {
          inputRefs.current[index + 1]?.focus();
        }

        // Check if all fields are filled
        if (newValues.every((v) => v) && newValues.length === OTP_LENGTH) {
          onComplete(newValues.join(""));
        }
      },
      [values, onComplete]
    );

    const handleKeyDown = useCallback(
      (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        // Move to previous field on backspace if current field is empty
        if (e.key === "Backspace" && !values[index] && index > 0) {
          inputRefs.current[index - 1]?.focus();
        }

        // Handle arrow keys
        if (e.key === "ArrowLeft" && index > 0) {
          e.preventDefault();
          inputRefs.current[index - 1]?.focus();
        }
        if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
          e.preventDefault();
          inputRefs.current[index + 1]?.focus();
        }
      },
      [values]
    );

    const handlePaste = useCallback(
      (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text");
        // Extract only digits from pasted content
        const digits = pastedData.replace(/\D/g, "").slice(0, OTP_LENGTH);

        if (!digits) return;

        const newValues = [...values];
        digits.split("").forEach((digit, i) => {
          newValues[i] = digit;
        });
        setValues(newValues);

        // Focus the next empty field or the last field
        const nextEmpty = newValues.findIndex((v) => !v);
        if (nextEmpty === -1 && digits.length === OTP_LENGTH) {
          // All fields filled, trigger completion
          onComplete(newValues.join(""));
          inputRefs.current[OTP_LENGTH - 1]?.focus();
        } else {
          inputRefs.current[
            nextEmpty !== -1 ? nextEmpty : OTP_LENGTH - 1
          ]?.focus();
        }
      },
      [values, onComplete]
    );

    const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
      // Select the content when focused
      e.target.select();

      // On mobile, wait for the keyboard to appear and the padding transition
      // to expand the scrollable area, then scroll the input into view.
      const target = e.target;
      setTimeout(() => {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 500);
    }, []);

    return (
      <InputContainer>
        {Array.from({ length: OTP_LENGTH }).map((_, index) => (
          <DigitInputWrap
            key={index}
            data-error={error}
            data-disabled={disabled}
          >
            <DigitInput
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={values[index]}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              onFocus={handleFocus}
              disabled={disabled}
              aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
              // First input gets autofocus and one-time-code autocomplete for iOS SMS autofill
              autoFocus={index === 0}
              autoComplete={index === 0 ? "one-time-code" : "off"}
            />
          </DigitInputWrap>
        ))}
      </InputContainer>
    );
  }
);

export default OtpInput;
